import React, { useState, useEffect, useRef, createContext } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { Link, withRouter } from 'react-router-dom';
import usePortalAuth from '../../../auth/usePortalAuth';

//------------Material UI------------//
import clsx from 'clsx';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import List from '@material-ui/core/List';
import CssBaseline from '@material-ui/core/CssBaseline';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import LinearProgress from '@material-ui/core/LinearProgress';
import IconButton from '@material-ui/core/IconButton';

import NotificationsIcon from '@material-ui/icons/Notifications';
import SecurityIcon from '@material-ui/icons/Security';
import SchoolIcon from '@material-ui/icons/School';
import SettingsIcon from '@material-ui/icons/Settings';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import MenuIcon from '@material-ui/icons/Menu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import '../Dashboard.css';

const SideNav = (props, location, history) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  const isActive = (value) => {
    if (props.location.pathname === value) {
      return 'activeSideNavLink';
    } else {
      return 'sideNavLink';
    }
  };
  
  const handleToggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    if (props.onCollapseChange) {
      props.onCollapseChange(newCollapsedState);
    }
  };

  //----SideNav Styles----//

  const drawerWidth = 240;
  const drawerWidthCollapsed = 64;

  const useStyles = makeStyles((theme) => ({
    root: {
      display: 'inline',
      height: '100vw',
      marginTop: '5px', // Moved up 20px from 25px
    },
    appBar: {
      backgroundColor: '#F4F7FC',
      zIndex: theme.zIndex.drawer + 1,
      boxShadow: 'none',
      border: 'none',
    },
    menuButton: {
      marginRight: 36,
    },
    hide: {
      display: 'none',
    },
    drawer: {
      width: isCollapsed ? drawerWidthCollapsed : drawerWidth,
      flexShrink: 0,
      color: 'white',
      zIndex: 1,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    drawerPaper: {
      marginTop: 65, // Moved down to 65px so it's visible below navbar
      width: isCollapsed ? drawerWidthCollapsed : drawerWidth,
      backgroundColor: theme.palette.sideNav.main,
      position: 'absolute',
      height: '100rem',
      overflow: 'hidden', // Always hide overflow to prevent text bleeding
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: theme.spacing(0, 1),
      ...theme.mixins.toolbar,
    },
    content: {
      flexGrow: 1,
      padding: theme.spacing(2),
    },
    list: {
      color: 'lightgrey',
      border: '1px, #061739',
      fontFamily: 'Montserrat, sans-serif',
      fontSize: '14px', // Increased from 12px
      marginRight: '0px',
      textDecoration: 'none',
      hover: {
        textDecoration: 'none',
      },
    },
    listItemText: {
      fontSize: '14px',
      fontWeight: 500,
    },
    listItemCollapsed: {
      justifyContent: 'center',
      padding: '12px 0',
      minHeight: '48px',
      width: drawerWidthCollapsed,
      maxWidth: drawerWidthCollapsed,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
    },
    listItemExpanded: {
      paddingLeft: '16px',
      minHeight: '48px',
      display: 'flex',
      alignItems: 'center',
    },
    collapseButton: {
      position: 'absolute',
      top: 10, // Back to 10px since drawer is now positioned correctly
      right: 8,
      color: 'rgba(255, 255, 255, 0.7)',
      '&:hover': {
        color: '#fff',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
    accountHeader: {
      padding: theme.spacing(2),
      borderBottom: '1px solid rgba(255,255,255,0.1)',
      marginBottom: theme.spacing(1),
      textAlign: isCollapsed ? 'center' : 'left',
    },
  }));

  const classes = useStyles();

  const translateText = (inputText, languageCode) => {
    if (typeof props.getTranslation === 'function') {
      const translatedText = props.getTranslation(inputText, languageCode);
      if (typeof translatedText === 'string' && translatedText.trim() !== '') {
        return translatedText;
      }
    }

    return inputText;
  };

  //console.log(props.preferredLanguage)

  return (
    <div id="sideNav" className={classes.root}>
      <CssBaseline />

      <Drawer
        className={classes.drawer}
        variant="permanent"
        classes={{
          paper: classes.drawerPaper,
        }}
        anchor="left"
      >
        {/* Collapse Button */}
        <IconButton 
          onClick={handleToggleCollapse}
          className={classes.collapseButton}
          size="small"
        >
          {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
        
        <Divider />
        
        {/* Account Name Header */}
        {props.accessLevel === 'AccountAdmin' && !isCollapsed && (
          <div className={classes.accountHeader}>
            <Typography 
              variant="caption" 
              style={{ 
                color: 'rgba(255,255,255,0.6)', 
                fontSize: '0.75rem',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Current Account
            </Typography>
            <Typography 
              variant="body2" 
              style={{ 
                color: '#fff', 
                fontSize: '0.9rem',
                fontWeight: 600,
                marginTop: '4px',
                wordBreak: 'break-word'
              }}
            >
              {props.selectedClient?.companyName || 
               props.accountAdminData?.find(company => company.clientId === props.selectedClient)?.companyName ||
               'Account Admin'}
            </Typography>
          </div>
        )}
        <div style={{ 
          paddingTop: '50px', // Give collapse button its own space
          width: '100%',
          overflow: 'hidden'
        }}>
          <List 
            className={classes.list} 
            style={{ 
              textDecoration: 'none',
              width: '100%',
              padding: 0
            }}
          >
            {props.accessLevel === 'AccountAdmin' && (
              <ListItem
                button
                className={`${isActive('/Dashboard/AccountManagement')} ${isCollapsed ? classes.listItemCollapsed : classes.listItemExpanded}`}
                key="AccountManagement"
                component={Link}
                to={'/Dashboard/AccountManagement'}
              >
                <ListItemIcon style={{ 
                  minWidth: isCollapsed ? 'auto' : 40, 
                  justifyContent: 'center',
                  color: 'inherit'
                }}>
                  <SecurityIcon style={{ fontSize: '1.2rem' }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={translateText('Account Management', props.preferredLanguage)}
                    primaryTypographyProps={{ 
                      style: { fontSize: '14px', fontWeight: 500, color: 'inherit' }
                    }}
                  />
                )}
              </ListItem>
            )}

            {(props.accessLevel === 'AccountAdmin' || props.accessLevel === 'CompanyAdmin') && (
              <ListItem
                button
                className={`${isActive('/Dashboard/CompanyOverview')} ${isCollapsed ? classes.listItemCollapsed : classes.listItemExpanded}`}
                key="CompanyOverview"
                component={Link}
                to={'/Dashboard/CompanyOverview'}
              >
                <ListItemIcon style={{ 
                  minWidth: isCollapsed ? 'auto' : 40, 
                  justifyContent: 'center',
                  color: 'inherit'
                }}>
                  <FontAwesomeIcon icon={['fa', 'chart-bar']} size="lg" />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={translateText('Company Overview', props.preferredLanguage)}
                    primaryTypographyProps={{ 
                      style: { fontSize: '14px', fontWeight: 500, color: 'inherit' }
                    }}
                  />
                )}
              </ListItem>
            )}

            <ListItem
              button
              data-tut={props.tourSideNavSelector}
              className={`${isActive('/Dashboard/History')} ${isCollapsed ? classes.listItemCollapsed : classes.listItemExpanded}`}
              key="History"
              component={Link}
              to={'/Dashboard/History'}
            >
              <ListItemIcon style={{ 
                minWidth: isCollapsed ? 'auto' : 40, 
                justifyContent: 'center',
                color: 'inherit'
              }}>
                <FontAwesomeIcon icon={['fas', 'history']} size="lg" />
              </ListItemIcon>
              {!isCollapsed && (
                <ListItemText 
                  primary={translateText('History', props.preferredLanguage)}
                  primaryTypographyProps={{ 
                    style: { fontSize: '14px', fontWeight: 500, color: 'inherit' }
                  }}
                />
              )}
            </ListItem>

            {!!props.training && (
              <ListItem
                button
                className={`${
                  props.accessLevel === 'AccountAdmin' || props.accessLevel === 'CompanyAdmin'
                    ? isActive('/Dashboard/AdminTrainingDashboard')
                    : isActive('/Dashboard/TrainingDashboard')
                } ${isCollapsed ? classes.listItemCollapsed : classes.listItemExpanded}`}
                key="Training"
                component={Link}
                to={
                  props.accessLevel === 'AccountAdmin' || props.accessLevel === 'CompanyAdmin'
                    ? '/Dashboard/AdminTrainingDashboard'
                    : '/Dashboard/TrainingDashboard'
                }
              >
                <ListItemIcon style={{ 
                  minWidth: isCollapsed ? 'auto' : 40, 
                  justifyContent: 'center',
                  color: 'inherit'
                }}>
                  <SchoolIcon style={{ fontSize: '1.2rem' }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={translateText('Training', props.preferredLanguage)}
                    primaryTypographyProps={{ 
                      style: { fontSize: '14px', fontWeight: 500, color: 'inherit' }
                    }}
                  />
                )}
              </ListItem>
            )}

            {(props.accessLevel === 'AccountAdmin' || props.accessLevel === 'CompanyAdmin') && (
              <ListItem
                button
                className={`${isActive('/Dashboard/Alerts')} ${isCollapsed ? classes.listItemCollapsed : classes.listItemExpanded}`}
                key="Alerts"
                component={Link}
                to={'/Dashboard/Alerts'}
              >
                <ListItemIcon style={{ 
                  minWidth: isCollapsed ? 'auto' : 40, 
                  justifyContent: 'center',
                  color: 'inherit'
                }}>
                  <NotificationsIcon style={{ fontSize: '1.2rem' }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={translateText('Alerts', props.preferredLanguage)}
                    primaryTypographyProps={{ 
                      style: { fontSize: '14px', fontWeight: 500, color: 'inherit' }
                    }}
                  />
                )}
              </ListItem>
            )}

            <ListItem
              button
              className={`${
                props.accessLevel === 'CompanyAdmin' || props.accessLevel === 'AccountAdmin'
                  ? isActive('/Dashboard/AnalyzeLinkAdmin')
                  : isActive('/Dashboard/AnalyzeLink')
              } ${isCollapsed ? classes.listItemCollapsed : classes.listItemExpanded}`}
              key={
                props.accessLevel === 'CompanyAdmin' || props.accessLevel === 'AccountAdmin'
                  ? 'AnalyzeLinkAdmin'
                  : 'AnalyzeLink'
              }
              component={Link}
              to={
                props.accessLevel === 'CompanyAdmin' || props.accessLevel === 'AccountAdmin'
                  ? '/Dashboard/AnalyzeLinkAdmin'
                  : '/Dashboard/AnalyzeLink'
              }
            >
              <ListItemIcon style={{ 
                minWidth: isCollapsed ? 'auto' : 40, 
                justifyContent: 'center',
                color: 'inherit'
              }}>
                <FontAwesomeIcon icon={['fas', 'link']} size="lg" />
              </ListItemIcon>
              {!isCollapsed && (
                <ListItemText 
                  primary={translateText('Link Analysis', props.preferredLanguage)}
                  primaryTypographyProps={{ 
                    style: { fontSize: '14px', fontWeight: 500, color: 'inherit' }
                  }}
                />
              )}
            </ListItem>

            {(props.accessLevel === 'CompanyAdmin' || props.accessLevel === 'AccountAdmin') && (
              <ListItem
                button
                className={`${isActive('/Dashboard/AdminConfiguration')} ${isCollapsed ? classes.listItemCollapsed : classes.listItemExpanded}`}
                key="AdminConfiguration"
                component={Link}
                to={'/Dashboard/AdminConfiguration'}
              >
                <ListItemIcon style={{ 
                  minWidth: isCollapsed ? 'auto' : 40, 
                  justifyContent: 'center',
                  color: 'inherit'
                }}>
                  <FontAwesomeIcon icon={['fas', 'cog']} size="lg" />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={translateText('Admin Configuration', props.preferredLanguage)}
                    primaryTypographyProps={{ 
                      style: { fontSize: '14px', fontWeight: 500, color: 'inherit' }
                    }}
                  />
                )}
              </ListItem>
            )}

            {/* <ListItem
              button
              className={isActive('/Dashboard/Configuration')}
              key="Configuration"
              component={Link}
              to={'/Dashboard/Configuration'}
            >
              <ListItemIcon className={classes.list} style={{ minWidth: 40 }}>
                <FontAwesomeIcon icon={['fas', 'sliders-h']} size="lg" />
              </ListItemIcon>
              <ListItemText>{translateText('User Configuration', props.preferredLanguage)}</ListItemText>
            </ListItem> */}

            {(props.accessLevel === 'CompanyAdmin' || props.accessLevel === 'AccountAdmin') && (
              <ListItem
                button
                className={`${isActive('/Dashboard/LinkSafetyRules')} ${isCollapsed ? classes.listItemCollapsed : classes.listItemExpanded}`}
                key="LinkSafetyRules"
                component={Link}
                to={'/Dashboard/LinkSafetyRules'}
              >
                <ListItemIcon style={{ 
                  minWidth: isCollapsed ? 'auto' : 40, 
                  justifyContent: 'center',
                  color: 'inherit'
                }}>
                  <FontAwesomeIcon icon={['fa', 'traffic-light']} size="lg" />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={translateText('Link Safety Rules', props.preferredLanguage)}
                    primaryTypographyProps={{ 
                      style: { fontSize: '14px', fontWeight: 500, color: 'inherit' }
                    }}
                  />
                )}
              </ListItem>
            )}
            {(props.accessLevel === 'CompanyAdmin' || props.accessLevel === 'AccountAdmin') && (
              <ListItem
                button
                className={`${isActive('/Dashboard/UserManagement')} ${isCollapsed ? classes.listItemCollapsed : classes.listItemExpanded}`}
                key="UserManagement"
                component={Link}
                to={'/Dashboard/UserManagement'}
              >
                <ListItemIcon style={{ 
                  minWidth: isCollapsed ? 'auto' : 40, 
                  justifyContent: 'center',
                  color: 'inherit'
                }}>
                  <FontAwesomeIcon icon={['fas', 'users']} size="lg" />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={translateText('User Management', props.preferredLanguage)}
                    primaryTypographyProps={{ 
                      style: { fontSize: '14px', fontWeight: 500, color: 'inherit' }
                    }}
                  />
                )}
              </ListItem>
            )}
          </List>
        </div>
      </Drawer>

      <div />
    </div>
  );
};
export default SideNav;
